import { Injectable } from '@angular/core';
import { Location, LocationsEntity } from './location';
import { Observable, BehaviorSubject, fromEvent } from 'rxjs';
import {  map, tap, filter, debounceTime, distinctUntilChanged, mergeMap } from 'rxjs/operators';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Subdivision } from '../subdivisions/subdivision';
import { LocationModel } from "src/app/location/location";
import { AuthService } from "src/app/auth.service";
import { environment } from '../../environments/environment';
import { MessagerComponent } from '../messager/messager/messager.component';
import { DesignsEntity } from '../design/design.model';
import { MatSnackBar } from '@angular/material';
import { Router } from '@angular/router';

// Row cap for the autocomplete dropdown feeds only. A 3-character 'contains' search over
// locmain's 231k rows would otherwise drag back tens of thousands of rows to render ~10.
// The "Go" buttons, which populate the grid, deliberately send no limit.
const AUTOCOMPLETE_LIMIT = 20;

@Injectable({
  providedIn: 'root'
})

export class LocationService {

  setTechForAxis(location_id: number, track_id: number) {
    return this.http.post(this.baseURL + '/rhAPI/jobEntry/setTechForAxis/' + location_id + '/' + track_id, { observe: 'body' });
  }
  
  addNoteToVCBjobs(jobs: any):Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/jobEntry/addVCBVillagesNote', {jobs: jobs}, { observe: 'body' });
  }

  resetSFOnForm(location_id: number) {
    return this.http.get(this.baseURL + '/rhAPI/locations/resetSFOnForm/' + location_id, { observe: 'body' });
  }
  createGeneratorJob(location_id: any, tech: number) {
    return this.http.post(this.baseURL + '/rhAPI/jobEntry/createGeneratorJob/' + location_id, {tech: tech}, { observe: 'body' });
  }

  getSWPPPinfo(location_id: number): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/locations/getSWPPPinfo/' + location_id, { observe: 'body' });
  }

  meritageChaCompliance(postJSON:any, builder:number) : Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/jobEntry/meritageCHAComplianceReportsUploader/' + builder, postJSON, { observe: 'body' });
  }

  meritageCstlCompliance(postJSON:any) : Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/jobEntry/meritageCSTLComplianceReportsUploader', postJSON, { observe: 'body' });
  }

  updateTripJobs(selectedJobIds: number[], local_job_id: any, reason?: string, newStatus?: string) {
    const body: any = { selectedJobIds: selectedJobIds, trip_id: local_job_id };
    if (reason) { body.reason = reason; }
    if (newStatus) { body.newStatus = newStatus; }
    return this.http.post(this.baseURL + '/rhAPI/jobEntry/setTrip', body , { observe: 'body' });
  }

  getTripJobs(tripId: number): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/jobEntry/getTripJobs/' + tripId, { observe: 'body' });
  }

  updateJobNotes(jobId: number, jobNotes: string): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/jobEntry/updateJobNotes/' + jobId, { job_notes: jobNotes }, { observe: 'body' });
  }

  carryOverTripJobs(previousTripId: number, newTripId: number): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/jobEntry/carryOverTripJobs', { previousTripId, newTripId }, { observe: 'body' });
  }

  // Copies the given fields (any of 'technitian' | 'schedule_date' | 'job_status') from a trip
  // parent onto every one of its child jobs. Reads the values server-side off the trip's own
  // row, so this always reflects what actually ended up persisted on the trip.
  propagateTripFields(tripId: number, fields: string[]): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/jobEntry/propagateTripFields/' + tripId, { fields }, { observe: 'body' });
  }

  // The trip a task belongs to (date as 'YYYY-MM-DD') and how many sibling tasks would move with it.
  getTripTaskMoveInfo(taskId: number): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/jobEntry/getTripTaskMoveInfo/' + taskId, { observe: 'body' });
  }

  // After a trip task was saved: 'trip' copies the given fields ('schedule_date' / 'technitian' /
  // 'contact') from the task onto the trip and its open tasks; 'detach' removes it from the trip.
  resolveTripTaskMove(taskId: number, action: 'trip' | 'detach', fields: string[] = []): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/jobEntry/resolveTripTaskMove/' + taskId, { action, fields }, { observe: 'body' });
  }

  createTripFromJob(job_id: number): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/jobEntry/createTripFromJob', { job_id }, { observe: 'body' });
  }

  addJobToTrip(job_id: number, trip_id: number): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/jobEntry/addJobToTrip', { job_id, trip_id }, { observe: 'body' });
  }

  // Edit-job "Trip?" on an existing job: new parent, this job as primary, plus task_ids.
  convertJobToTrip(job_id: number, task_ids: number[]): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/jobEntry/convertJobToTrip', { job_id, task_ids }, { observe: 'body' });
  }

  saveTripTasks(trip_id: number, add_ids: number[], remove_ids: number[]): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/jobEntry/saveTripTasks', { trip_id, add_ids, remove_ids }, { observe: 'body' });
  }

  // Every Scheduled/Projected trip at the location, any date.
  getOpenTrips(location_id: any): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/jobEntry/getFutureTrips/' + location_id, { observe: 'body' });
  }


  resetDueDateCompliance(job_id: any):Observable<any>{
    return this.http.post(this.baseURL + '/rhAPI/jobEntry/resetDueDateCompliance/' + job_id, {}, { observe: 'body' });
  }

  updateDueDateCompliance(job_id: number,
     customer_id: number,
     schedule_date: any,
     customer_jobtype: string):Observable<any> {

    return this.http.post(this.baseURL + '/rhAPI/jobEntry/updateDueDatesCompliance/' + job_id, {
      "customer_id": customer_id,
      "schedule_date": schedule_date,
      "customer_jobtype": customer_jobtype
      }, { observe: 'body'});
  }

  // {startdate, customer_id} for a compliance job, keyed on job_id (works from
  // both the location and schedule pages). Used by EditJobDialog to schedule a
  // Meritage CHA/CSTL PendInfo->Scheduled change onto the start date.
  getComplianceStartInfo(job_id: number):Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/jobEntry/complianceStartInfo/' + job_id, { observe: 'body' });
  }

  drhortonGRV(obj):Observable<any> {
    console.log(obj);
    return this.http.post(this.baseURL + '/rhAPI/csvfunc/drHortonGRV', obj, {observe: 'body'});
  }

  trueHomesPermitReport(obj):Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/csvfunc/trueHomesPermitReport', obj, {observe: 'body'});
  }

  drhortonCLT(obj):Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/csvfunc/drHortonCLT', obj, {observe: 'body'});
  }

  drhortonINDY(obj):Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/csvfunc/drHortonIndy', obj, {observe: 'body'});
  }

  drhortonLOU(obj):Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/csvfunc/drHortonLou', obj, {observe: 'body'});
  }

  drhortonSouthRDU(obj):Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/csvfunc/drHortonRDUSouth', obj, {observe: 'body'});
  }

  drhortonEastRDU(obj):Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/csvfunc/drHortonRDUEast', obj, {observe: 'body'});
  }

  createSWPPjobs(locid: number): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/jobEntry/createSWPPjobs/' + locid, {}, { observe: 'body' });
  }
  
  addConditionPhotos(job: any) {
    return this.http.post('/m/upload-photos-form', { job }, { responseType: 'text' });
  }
  removeIndividualAudit(employee_id: number, job_id: any): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/jobEntry/removeIndividualAudits/' + job_id + '/' + employee_id, {}, { observe: 'body' });
  }
  
  getIndividualAudits(job_id: any): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/jobEntry/getIndividualAudits/' + job_id, { observe: 'body' });
  }

  addRebate(name: string, type: string) {
    return this.http.post(this.baseURL + '/rhAPI/locations/addRebate', {rebate: name, type: type}, { observe: 'body' });
  }

  getRebateSelections(): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/locations/getRebateOptions', { observe: 'body' });
  }

  getProdOrCust(location_id: number): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/tracks/getProdOrCust/' + location_id, { observe: 'body' });
  }
  
  updateOtherTrackJobs(obj: any, location_id: number, updateTech: number): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/locations/updateOtherTrackJobs/' + location_id + '/' + updateTech, obj,  { observe: 'body' });
  }


  getSubdivisionContact(location_id: any): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/jobEntry/getSubdivisionContact/' + location_id,  { observe: 'body' });
  }
  uploadAuditPhoto(pic: File, name: string) {
    
    const formData = new FormData();
    formData.append('file', pic);
    formData.append('name', name);
    
    formData.forEach((value, key) => {
    });

    return this.http.post(this.baseURL + '/rhAPI/uploadAuditPhoto', formData);

  }
  
  runComplianceAutomation(location_id: any):Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/jobEntry/runComplianceAutomation/' + location_id, {}, { observe: 'body' });
  }

  getFinalPassed(location_id: any): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/jobEntry/getFinalPassed/' + location_id,  { observe: 'body' });
  }

  // Card tQPtadd5 - does this location have a Pass/Complete pre-drywall job on file?
  getPdwCompleted(location_id: any): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/jobEntry/getPdwCompleted/' + location_id,  { observe: 'body' });
  }

  getOneJob(job_id: any):Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/jobEntry/getOneJob/' + job_id, { observe: 'body' });
  }
  getClimateZone(location_id: any):Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/locations/getHeroMandatoryInfo/' + location_id, {observe: 'body'});
  }
  resetThermalRequired(location_id: any): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/locations/resetThermalRequired/' + location_id, {}, { observe: 'body' });
  }
  addAudit( auditObj: any): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/jobEntry/addAudit', auditObj, {observe: 'body'});
  }

  saveIndividualAudit(auditObj: any, job_id: number): Observable<any> { 
    return this.http.post(this.baseURL + '/rhAPI/jobEntry/insertIndividualAudits/' + job_id, auditObj, {observe: 'body'});
  } 

  saveAudit(job_id: any, auditObj: any){
    return this.http.post(this.baseURL + '/rhAPI/jobEntry/saveAudit/' + job_id, auditObj, {observe: 'body'});
  }

  getAuditForJobID(job_id: any): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/jobEntry/getAuditByJobID/' + job_id, {observe: 'body'});
  }
  insertESMFNC(location_id: number): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/raterform/insertESMFNCdata/' + location_id, {}, { observe: 'body' });
  }


  getAllNamedTags(job_id: any): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/jobEntry/getAllNamedTags/' + job_id, {observe: 'body'});
  }


  private baseURL = environment.baseUrl;

  public jobConditionPhotoslink = this.baseURL + '/m/upload-photos-form';

  private subject = new BehaviorSubject<any[]>([]); //subject for initial locations
  private loadingSubject = new BehaviorSubject<boolean>(false);

  private bigSubject = new BehaviorSubject<any[]>([]); //subject for all locations

  gridObject$: Observable<Location[]> = this.subject.asObservable(); //observable for intial locations
  allLoc$: Observable<Location[]> = this.bigSubject.asObservable(); //observable for ALL locations
  loadingSubject$: Observable<boolean> = this.loadingSubject.asObservable();

  private jobsubject = new BehaviorSubject<any[]>([]);
  detailJob$ : Observable<any[]> = this.jobsubject.asObservable();

  //flag for user deep search
  deepSearchIsOn = false;

  //holds current array values of deep search
  deepSearchLocs = [];



  isLoading = true;
  loadingAll = false;
  //public allLocs : Observable<Location[]>;
  allLoaded = new BehaviorSubject(false);

  allLoadError: any;
  locArray = [];

  jobsForLoc;


  constructor(private http: HttpClient,
    private messager: MessagerComponent,
    private auth: AuthService,
    private snackBar: MatSnackBar,
    private router: Router,) { }

  init() {

    const locationHttp$ = this.getLocations();
    this.isLoading = true;
    locationHttp$.pipe(
      map(res => res),
      tap(() => {
        this.isLoading = false;
      })
    ).subscribe(
      res => {
        this.subject.next(res.Locations);
        this.locArray = res.Locations;
      }
    );

    const allLocation$ = this.loadAllJobs();
    allLocation$.pipe(
      map(res => res),
      tap(() => {
        this.allLoaded.next(true);
        this.loadingAll = true;
      })
    ).subscribe(
      res => {
        this.bigSubject.next(res.Locations);

        this.subject.next(res.Locations);
        if(this.deepSearchIsOn){
          this.subject.next(this.deepSearchLocs);
        }
        this.locArray = res.Locations;
      }, err => {
        this.allLoadError = true;
      });

  }

  getInitial() {
    return this.gridObject$;
  }

  resetFilter() {
    this.subject.next(this.locArray);
    this.deepSearchIsOn = false;
  }

  getAllLocs() {
    return this.allLoc$;
  }

  getAllLoaded() {
    return this.allLoaded;
  }

  updateJobs(id: number, filter: number) {
    this.loadingSubject.next(true);

    const jobhttp$ = this.getJobsForLocation(id, filter).subscribe(
      res => {
        this.loadingSubject.next(false);
        this.jobsubject.next(res.Jobs);
      }
    )
  }

  updateJobsSorted(id: number, filter: number) {
    this.loadingSubject.next(true);

    const jobhttp$ = this.getJobsForLocationSorted(id, filter).subscribe(
      res => {
        this.loadingSubject.next(false);
        this.jobsubject.next(res.Jobs);
      }
    )

  }

  getJobs(id: number, filter: number) {
    this.jobsubject.next([]);
    this.updateJobs(id, filter);
    return this.detailJob$;
  }

  getJobsSorted(id: number, filter: number) {
    this.jobsubject.next([]);
    this.updateJobsSorted(id, filter);
    return this.detailJob$;
  }

  getTag(tag_id):Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/jobEntry/getTag/' + tag_id, { observe: 'body' });
  }
  updateTagUseCount(idArray: any[]) {
    return this.http.post(this.baseURL + '/rhAPI/jobEntry/updateTagCount', idArray, { observe: 'body' });
  }
  getTags():Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/jobEntry/getTags/', { observe: 'body' });
  }



  getApplogs(job_id: any, locid: any): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/jobEntry/getApplogs/' + job_id + '/' + locid, { observe: 'body' });
  }


  getMfTemplateName(mftemplate: any) : Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/jobEntry/getMultifamily/' + mftemplate, { observe: 'body' });
  }

  getLocations(): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/locations/getOneHundred', { observe: 'body' });
  }

  // Every location_id that has at least one live Extra Notes post. One small list, so the
  // grid (which can hold every location once "All Locations Loaded") can flag rows without
  // touching any of the hand-written locmain search queries.
  getLocationIdsWithExtraNotes(): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/locations/withExtraNotes', { observe: 'body' });
  }


  getElecUtility(subdivision_id) : Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/locations/getElecUtility/' + subdivision_id, {observe: 'body'});
  }

  getRegion(subdivision_id): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/locations/getRegion/' + subdivision_id, {observe: 'body'});
  }

  getScope(subdivision_id) : Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/locations/getScope/' + subdivision_id, {observe: 'body'});
  }

  getJobsForLocation(id, filter = 0): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/jobsByLocation/' + id + '/' + filter, { observe: 'body' });
  }

  getJobsForLocationSorted(id, filter = 0): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/getJobsByLocationSorted/' + id + '/' + filter, { observe: 'body' });
  }
  


  copyJob(job_id: any): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/jobEntry/copyJob/' + job_id, {}, { observe: 'body' });
  }

  copyJobWithTrack(job_id: any): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/jobEntry/copyJobTrack/' + job_id, {}, { observe: 'body' });
  }

  getJobDisplayDetails(job): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/jobEntry/getJobDisplayDetails/' + job.job_id, { observe: 'body' });
  }

  createDriveFolder(object): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/driveCreateFolder', object, { observe: 'body' });
  }

  loadAllJobs(): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/locations', { observe: 'body' });
  }

  getPaymentHold(customer_name): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/customers/getPaymentHold/' + customer_name, { observe: 'body' });
  }

  getCompanyLocations(customer_id): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/jobEntry/getCompanyLocations/' + customer_id, { observe: 'body' });
  }

  getPlansLocations(plan, mode?: string): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/locations/getAllPlans/' + this.searchTerm(plan.plan_name) + this.searchQuery(mode), { observe: 'body' });
  }




  saveFolderID(locationId: number, changes): void {

    const locations = this.subject.getValue();

    const locationIndex = locations.findIndex(location => location.location_id === locationId);
    const newLocationList = locations.slice(0);


    newLocationList[locationIndex] = {
      ...locations[locationIndex],
      ...changes
    };


    this.subject.next(newLocationList);

  }



  saveEdit(locationId: number, changes, version: number = 0): Observable<any> {

    const locations = this.subject.getValue();
    const locationIndex = locations.findIndex(location => location.location_id === locationId);
    const newLocationList = locations.slice(0);

    newLocationList[locationIndex] = {
      ...locations[locationIndex],
      ...changes
    };

    this.subject.next(newLocationList);
    if(version == 1){

      return this.http.post(this.baseURL + '/rhAPI/locations/editFlags/' + locationId, changes).pipe(
        tap((event) => {
          const message = event.Message as unknown as string;
          if (event.Error) {
            this.messager.openSnackBar(message, 'Close', 'errorAlert');
            this.subject.next(locations);
          } else {
            this.messager.openSnackBar(message, '', 'successAlert');
          }
        })
      );


    }else {
      return this.http.post(this.baseURL + '/rhAPI/locations/edit/' + locationId, changes).pipe(
        tap((event) => {
          const message = event.Message as unknown as string;
          if (event.Error) {
            this.messager.openSnackBar(message, 'Close', 'errorAlert');
            this.subject.next(locations);
          } else {
            this.messager.openSnackBar(message, '', 'successAlert');
          }
        })
      );
    }
  }

  // Checks whether a date (YYYY-MM-DD) falls on a blackout date for the
  // location's region, so the UI can warn before scheduling on it. The optional
  // technician narrows CSTL checks to the job's WIL/MYR sub-region. The optional
  // customer job lets the backend skip the warning for non-site-visit
  // (compliance/office) jobtypes, which blackout dates don't apply to.
  checkBlackoutDate(locationId: number, date: string, technitian?: number, customerJob?: number): Observable<any> {
    const params = [];
    if (technitian !== null && technitian !== undefined) { params.push('technitian=' + technitian); }
    if (customerJob !== null && customerJob !== undefined) { params.push('customer_job=' + customerJob); }
    const qs = params.length > 0 ? '?' + params.join('&') : '';
    return this.http.get<any>(this.baseURL + '/rhAPI/checkBlackoutDate/' + locationId + '/' + date + qs);
  }

  checkSamplingApplication(locationId: number, jobId?: number, customerJob?: number): Observable<any> {
    const params = [];
    if (jobId !== null && jobId !== undefined) { params.push('job_id=' + jobId); }
    if (customerJob !== null && customerJob !== undefined) { params.push('customer_job=' + customerJob); }
    const qs = params.length > 0 ? '?' + params.join('&') : '';
    return this.http.get<any>(this.baseURL + '/rhAPI/locations/checkSamplingApplication/' + locationId + qs);
  }

  saveJobEdit(jobid: number, changes, updateDueDate: boolean = false, newTags: any[] = []): Observable<any> {

    let data = {
      ...changes,
      updateDueDate: updateDueDate,
      new_tags: newTags  
    }

    return this.http.post(this.baseURL + '/rhAPI/editJob/' + jobid, data).pipe(
      tap((event) => {
        const message = event.Message as unknown as string;
        if (event.Error) {
          this.messager.openSnackBar(message, 'Close', 'errorAlert');
        } else {
          this.messager.openSnackBar(message, '', 'successAlert', 5000);
        }
      })
    );

  }

  updateViewStreetAddress(newRows): void {

    const rows = Array.from(newRows);
    this.deepSearchLocs = rows;
    this.deepSearchIsOn = true;
    this.subject.next(newRows);

  }

  // Search-mode plumbing for the Locations "Deep Searches" panel.
  // mode is optional everywhere: callers that omit it (the location-edit dialog, customer-jobs,
  // job entry, ...) keep today's prefix-match behavior, because the backend defaults to 'starts'.
  // limit is only sent by the autocomplete feeds, so the "Go" buttons keep returning the full set.
  private searchQuery(mode?: string, limit?: number): string {
    const parts = [];
    if (mode && mode !== 'starts') { parts.push('mode=' + encodeURIComponent(mode)); }
    if (limit) { parts.push('limit=' + limit); }
    return parts.length ? '?' + parts.join('&') : '';
  }

  // The search term goes in the URL path, so it must be encoded — a '/' or '#' in a
  // subdivision name breaks the route otherwise.
  private searchTerm(value): string {
    return encodeURIComponent(value == null ? '' : String(value));
  }

  updateViewStreetAddressManual(name, mode?: string): Observable<any> {

    return this.http.get(this.baseURL + '/rhAPI/locations/getAllAddress/' + this.searchTerm(name) + this.searchQuery(mode), { observe: 'body' });

  }

  updateViewSubdivisionManual(name, mode?: string): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/locations/getAllSub/' + this.searchTerm(name) + this.searchQuery(mode), { observe: 'body' });
  }

  updateRegisteryIdManual(name, mode?: string): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/locations/getAllByRegisteryId/' + this.searchTerm(name) + this.searchQuery(mode), { observe: 'body' });
  }

  updateInvoiceNoManual(name, mode?: string): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/locations/getAllByInvoiceNo/' + this.searchTerm(name) + this.searchQuery(mode), { observe: 'body' });
  }

  updatePoNumberoManual(name, mode?: string): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/locations/getAllByPoNumber/' + this.searchTerm(name) + this.searchQuery(mode), { observe: 'body' });
  }

  addOne(changes): Observable<any> {

    return this.http.post(this.baseURL + '/rhAPI/locations/createWithDriveFolder', changes).pipe(
      tap((event) => {
        const message = event.Message as unknown as string;
        if (event.Error) {
          this.messager.openSnackBar(message, 'Close', 'errorAlert');
          //this.subject.next(locations);
        } else {

          this.messager.openSnackBar(message, '', 'successAlert');
        }
      })
    )
  }



  getCustomerJobs():Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/customrjobs/getAllCustomerJobsList', { observe: 'body' });
  }

  addOneFrontend(changes): void {

    const locations = this.subject.getValue();
    const newLocationList = locations.slice(0);
    newLocationList.unshift(changes);
    this.subject.next(newLocationList);

  }

  updateCancelledStatus(job_id: number): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/jobEntry/cancelledStatus/' + job_id, {}, { observe: 'body' });
  }

  updateFolderStatus(location_id: number, folderID: string): void {

    const locations = this.subject.getValue();
    let number = locations.findIndex(element => element.location_id = location_id);
    locations[number].folderid = folderID;
    this.subject.next(locations);

  }



  addJob(changes, tags = []): Observable<any> {

    const data = {
      ...changes,
      new_tags: tags
    };

    return this.http.post(this.baseURL + '/rhAPI/addNewJob/create', data).pipe(
      tap((event) => {
        const message = event.Message as unknown as string;
        if (event.Error) {
          this.messager.openSnackBar(message, 'Close', 'errorAlert');
        } else {
          this.messager.openSnackBar(message, '', 'successAlert', 5000);
        }
      })
    );

  }

  //11-30
  // PICK UP HERE - look at this function for save error


  getOneLocation(locid): Observable<any> {

    return this.http.get(this.baseURL + '/rhAPI/locations/edit/' + locid).pipe(tap((event) => {
      const message = event.Message as unknown as string;
      if (event.Error) {
        this.messager.openSnackBar(message, 'Close', 'errorAlert');
      } else {
        //this.messager.openSnackBar(message, 'Editing...', 'successAlert');
      }
    }));
  }

  getTrack(location_id): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/locations/getTrack/' + location_id, { observe: 'body' });
  }

  getTracks(): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/locations/getTracks', { observe: 'body' });
  }

  addTrackJobs(track, builder, locationid, obj, socketid?): Observable<any> {
    const headers = new HttpHeaders({'x-socket-id': socketid || null});
    return this.http.post(this.baseURL + '/rhAPI/locations/applyTrackJobs/' + track + '/' + builder + '/' + locationid, obj, { observe: 'body', headers: headers });
  }
  
  addTrackJobsCentury(locationid, obj): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/locations/applyTrackJobsCentury/' + locationid, obj, { observe: 'body' });
  }

  addTrackJobsTaylorMorrison(locationid, obj): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/tracks/applyTrackJobsTaylorMorrison/' + locationid, obj, { observe: 'body' });
  }

  addTrackJobsTaylorMorrisonHERSHERO(locationid, obj): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/tracks/applyTrackJobsTaylorMorrisonHERSHERO/' + locationid, obj, { observe: 'body' });
  }

  // The handler hardcodes Meritage Coastal's customer job ids, so no builder id is sent.
  addTrackJobsMeritageCoastal(locationid, obj): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/tracks/applyTrackJobsMeritageCoastal/' + locationid, obj, { observe: 'body' });
  }

  // Trip-jobs version of the Meritage Coastal track (tracks.id 40); ids are hardcoded server-side.
  addTrackJobsMeritageCoastalTrips(locationid, obj): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/tracks/applyTrackJobsMeritageCoastalTrips/' + locationid, obj, { observe: 'body' });
  }

  addTrackJobsMeritageRDUHVG(locationid, obj): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/tracks/applyTrackJobsMeritageRDUHVG/' + locationid, obj, { observe: 'body' });
  }

  addTrackJobsDRHTRI(locationid, obj): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/locations/applyTrackJobsDRHTRI/' + locationid, obj, { observe: 'body' });
  }

  addTrackJobsDRHTRICode(locationid, obj): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/locations/applyTrackJobsDRHTRICode/' + locationid, obj, { observe: 'body' });
  }


  addTrackJobsBeechwood(locationid, obj): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/locations/applyTrackJobsBeechwood/' + locationid, obj, { observe: 'body' });
  }

  addTrackJobsBeechwoodHERSHERO(locationid, obj): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/locations/applyTrackJobsBeechwoodHERSHERO/' + locationid, obj, { observe: 'body' });
  }

  addTrackJobsHeroCustPrelim(locationid, custid): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/locations/applyTrackJobsHEROCustPrelim/' + locationid + '/' + custid, {}, { observe: 'body' });
  }

  addTrackJobsWaxhawLanding(locationid, obj): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/tracks/applyTrackJobsWaxhaw/' + locationid, obj, { observe: 'body' });
  }


  updateTrackDatesWaxhaw(locid, jobid): Observable<any> {
    return this.http.post(this.baseURL + `/rhAPI/location/updateTrackDatesWaxhaw/${locid}/${jobid}`, { observe: 'body' });
  }
  

  addTrackJobsBratton(locationid, obj): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/tracks/applyTrackJobsBratton/' + locationid, obj, { observe: 'body' });
  }

  // Card UyFfdPvC - Keen Building Company (tracks.id 37). The handler hardcodes
  // Keen's customer job ids, so no builder id is sent.
  addTrackJobsKeen(locationid, obj): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/tracks/applyTrackJobsKeen/' + locationid, obj, { observe: 'body' });
  }
  
  updateTrackJobDatesBratton(locid, jobid): Observable<any> {
    return this.http.post(this.baseURL + `/rhAPI/location/updateTrackDatesBratton/${locid}/${jobid}`, { observe: 'body' }); 
  }


  addTrackJobsBrighton(locationid, obj): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/tracks/applyTrackJobsBrightonTrack/' + locationid, obj, { observe: 'body' });
  }

  updateTrackDatesBrighton(locid, jobid): Observable<any> {
    return this.http.post(this.baseURL + `/rhAPI/location/updateTrackDatesBrighton/${locid}/${jobid}`, { observe: 'body' });
  }
  
  addTrackJobsBlairPlace(locationid, obj): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/tracks/applyTrackJobsBlairPlace/' + locationid, obj, { observe: 'body' });
  }

  // Carol Golden bills under ~8 separate customers3 rows, so the handler resolves
  // the custmrjobs rows from the location's builder - customer_id is required.
  addTrackJobsCarolGolden(locationid, obj, customer_id): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/tracks/applyTrackJobsCarolGolden/' +
       locationid + '/' + customer_id, obj, { observe: 'body' });
  }

  addTrackJobsRedCedar(locationid, obj, customer_id): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/tracks/applyTrackJobsRedCedar/' +
       locationid + '/' 
       + customer_id, obj, { observe: 'body' });
  }


  addTrackJobsMillCreek(locationid, obj, customer_id): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/locations/applyTrackJobsMillCreekRes/' +
       locationid + '/' + customer_id, obj, { observe: 'body' });
  }

  updateTrackDatesBlairPlace(locid, jobid): Observable<any> {
    return this.http.post(this.baseURL + `/rhAPI/location/updateTrackDatesBlairPlace/${locid}/${jobid}`, { observe: 'body' });
  }

  deleteLocation(id: number): void {

    const locations = this.subject.getValue();
    const locIndex = locations.findIndex(location => location.location_id === id);

    const newLocs = locations.slice(0);

    newLocs.splice(locIndex, 1);
    this.subject.next(newLocs);

  }



  getThermalRequired(location_id: any):Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/locations/getThermalRequired/' + location_id,{ observe: 'body' });
  }

  getLocationForJobs(id: number): Observable<any> {
    return this.http.get<any>(this.baseURL + '/rhAPI/locations/edit/' + id, { observe: 'body' }).pipe(
      map(res => res.Location));
  }

  jobupdater(obj): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/csvfunc/jobUpdater', obj, {observe: 'body'});
  }

  jobimporter(obj): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/csvfunc/jobImporter', obj, { observe: 'body' });
  }

  sendAuditEmail(location_id: any, job_id: any): Observable<any>{
    this.messager.openSnackBar('Sending audit email......', '', 'successAlert');
    return this.http.get(this.baseURL + '/rhAPI/locations/sendAuditEmail/' + location_id + '/' + job_id, {observe: 'body'});
  }

  locimporter(obj): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/csvfunc/locationImporter', obj, { observe: 'body' });
  }

  premiseno(obj): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/csvfunc/premiseNumberTemplate', obj, {observe: 'body'});
  }

  drhortonMyrtle(obj): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/csvfunc/drHortonMyrtle', obj, {observe: 'body'});
  }
  
  drhortonNOOG(obj): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/csvfunc/drHortonNOOG', obj, {observe: 'body'});
  }


  drhortonKNX(obj, customer): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/csvfunc/drHortonKnoxville/' + customer, obj, {observe: 'body'});
  }

  drhortonCinci(obj): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/csvfunc/drHortonCinci', obj, {observe: 'body'});
  }

  getTimeOff(): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/jobEntry/getTimeOffToday', {observe: 'body'});
  }

  updateESMFNC(location_id): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/locations/updateESMFNCneeded/' + location_id, {}, {observe: 'body'});
  }

  vendorSuite(obj, type, customerId): Observable<any> {
    const body = { ...obj, customer_id: customerId };
    return this.http.post(this.baseURL + '/rhAPI/csvfunc/vendorSuiteTemplate/' + type, body, {observe: 'body'});
  }

  getUploaderCustomers(): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/csvfunc/uploaderCustomers', {observe: 'body'});
  }

  getAllAddresses(street): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/locations/getAllAddress/', { observe: 'body' });
  }

  updateEpo(job): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/jobEntry/updateEpoRequested/' + job.job_id, job, {observe: 'body'});
  }

  getMultifamilyTemplates(name): Observable<any> {
    return this.http.get<any>(this.baseURL + '/rhAPI/multifamily/getTemplatesByName/' + name);
  }

  getSubdivisionsDropdown(name: string, mode?: string): Observable<Subdivision[]> {
    return this.http.get<any>(this.baseURL + '/rhAPI/locations/getSubdivisions/' + this.searchTerm(name) + this.searchQuery(mode, AUTOCOMPLETE_LIMIT), { observe: 'body' }).pipe(
      map(res => res.Subdivisions));
  }

  getAddressFilterNames(name: string, mode?: string): Observable<any[]> {
    return this.http.get<any[]>(this.baseURL + '/rhAPI/locations/getAllAddress/' + this.searchTerm(name) + this.searchQuery(mode, AUTOCOMPLETE_LIMIT));

  }

  getRegisteryFilterDropdown(registeryNO:Number, mode?: string): Observable<any[]>{
    return this.http.get<any[]>(this.baseURL + '/rhAPI/locations/getAllByRegisteryId/' + this.searchTerm(registeryNO) + this.searchQuery(mode, AUTOCOMPLETE_LIMIT));
  }

  getInvoiceNoDropdown(ivnoiceNum:string, mode?: string): Observable<any[]>{
    return this.http.get<any[]>(this.baseURL + '/rhAPI/locations/getAllByInvoiceNo/' + this.searchTerm(ivnoiceNum) + this.searchQuery(mode, AUTOCOMPLETE_LIMIT));
  }
  getPoNumberDropdown(ivnoiceNum:string, mode?: string): Observable<any[]>{
    return this.http.get<any[]>(this.baseURL + '/rhAPI/locations/getAllByPoNumber/' + this.searchTerm(ivnoiceNum) + this.searchQuery(mode, AUTOCOMPLETE_LIMIT));
  }

  getBuilderDropdown(name, mode?: string): Observable<any> {
    return this.http.get<any>(this.baseURL + '/rhAPI/customers/' + this.searchTerm(name) + this.searchQuery(mode, AUTOCOMPLETE_LIMIT), { observe: 'body' });
  }

  getBuilder(customer_id): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/customers/edit/' + customer_id, { observe: 'body' });
  }

  getPlanDropdown(name): Observable<any> {
    return this.http.get<any>(this.baseURL + '/rhAPI/locations/getPlans/' + name, { observe: 'body' });
  }

  submitHVACemail(designer) : Observable<any>{
    return this.http.post(this.baseURL + '/rhAPI/emails/hvac', designer, {observe: 'body'});
  }

  getApplogData(location_id): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/locations/getApplogData/' + location_id, { observe: 'body' });
  }

  getSubmittedHVAC(lid): Observable<any>{
    return this.http.get(this.baseURL + '/rhAPI/locations/getSubmission/' + lid, {observe: 'body'});
  }

  getHvacData(lid): Observable<any>{
    return this.http.get(this.baseURL + '/rhAPI/locations/getHVACdata/' + lid, {observe: 'body'});
  }

  getHVACdataLegacy(lid): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/locations/getHVACdataLegacy/' + lid, {observe: 'body'});
  }

  getHVACequipValues(sbmt_dsn_id): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/locations/getHVACequipBySbmtId/' + sbmt_dsn_id, {observe: 'body'});
  }

  getRaterHvacReview(id): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/location/getRaterHVAC/' + id, {observe: 'body'});
  }

  getEkotropeProjectId(id): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/ekotrope/getEkoProjectID/' + id, {observe: 'body'});
  }

  getEkoHvacReview(id): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/ekotrope/reviewPopup/' + id, {observe: 'body'});
  }

  createTag(obj): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/locations/createTag', obj, {observe: 'body'});
  }

  getSubFilter(name: string, mode?: string): Observable<any>{
    return this.http.get(this.baseURL + '/rhAPI/locations/getAllSub/' + this.searchTerm(name) + this.searchQuery(mode), {observe: 'body'});

  }

  resetDesign(location_id): Observable<any>{
    return this.http.post(this.baseURL + '/rhAPI/locations/resetDesign/' + location_id, {}, {observe:'body'});
  }

  resetRem(location_id): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/locations/runNumberReset/' + location_id, {}, {observe:'body'});
  }

  designReview(obj): Observable<any>{
    return this.http.post(this.baseURL + '/rhAPI/locations/designReview', obj, {observe:'body'});
  }

  hvacNotes(id, obj): Observable<any>{
    return this.http.post(this.baseURL + '/rhAPI/locations/hvacNotes/' + id, obj, {observe:'body'});
  }

  updateHVACBasics(sbmtid, obj): Observable<any>{
    return this.http.post(this.baseURL + '/rhAPI/locations/updateHVACBasics/' + sbmtid, obj, {observe:'body'});
  }

  setMissingInfo(id):Observable<any>{
    return this.http.post(this.baseURL + '/rhAPI/locations/setMissingInfoDate/' + id, {}, {observe:'body'});
  }

  checkDuplicateSA(street_address, location_id = 0): Observable<any> {
    // Query params (not a path segment) so addresses containing '/' or '#'
    // don't break routing; location_id lets edit mode skip self-matching.
    const params = new HttpParams()
      .set('address', street_address)
      .set('location_id', String(location_id || 0));
    return this.http.get(this.baseURL + '/rhAPI/locations/checkForDuplicatesSA', { params, observe: 'body' });
  }

  checkDuplicateSubLot(subdivision_name, lot): Observable<any>{
    return this.http.get(this.baseURL + '/rhAPI/locations/checkForDuplicatesSubLot/' + subdivision_name + '/' + lot, {observe: 'body'});
  }

  setCertPrintedDate(id): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/locations/setCertPrintedDate/' + id, {}, {observe:'body'});
  }

  rebate3update(id): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/locations/rebate3update/' + id, {}, {observe:'body'});
  }


  swpppInsert(location_id, cust_id, obj): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/locations/insertSWPPP/' 
      + location_id + '/' + cust_id, obj, {observe: 'body'});
  }

  swpppEnd(location_id): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/locations/endSWPPP/' 
      + location_id, {}, {observe: 'body'});
  }

  getLastRaterform(location_id): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/raterform/getLastHVAC/' + location_id, {observe:'body'} );
  }

  getContractorsDropdown(name: string): Observable<any> {

    return this.http.get<any>(this.baseURL + '/rhAPI/locations/getContractors/' + name, {observe: 'body'})
  }

  getSubdivisionJobs(subdivision_id): Observable<any> {
    return this.http.get<any>(this.baseURL + '/rhAPI/jobEntry/getSubdivisionJobs/' + subdivision_id, {observe: 'body'});
  }

  getCustomerJobsSubdivision(name: string, id: number, override: boolean, scope:string, subJobsFound: boolean, subdivision_id ): Observable<any[]> {
    return this.http.get<any[]>(this.baseURL + '/rhAPI/jobEntry/getSubdivisionJobs/' + subdivision_id, {observe: 'body'});
  }

  getCustomerJobsDropdown(name: string, id: number, override: boolean, scope:string): Observable<any[]> {

    let escapedScope = scope.replace("/", "&");

      if(override || escapedScope.length < 1 || scope.toLowerCase().includes('error') ){
        return this.http.get<any[]>(this.baseURL + '/rhAPI/getCustomerJobs/' + id + '/' + name, { observe: 'body' });
      } else {
        return this.http.get<any[]>(this.baseURL + '/rhAPI/getCustomerJobsFiltered/' + escapedScope + '/' + name, { observe: 'body' });
      }

  }

  addCSVReport(obj, cust): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/jobEntry/csvreport/' + cust, obj, {observe: 'body'});
  }

  getEmployeesDropdown(name: string): Observable<any> {
    return this.http.get<any>(this.baseURL + '/rhAPI/getEmployeesByInitial/' + name, { observe: 'body' }).pipe(
      map(res => res.Initials)
    );
  }

  getComplianceTracks(): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/locations/getComplianceTracks', {observe: 'body'});
  } 

  getLocErrors(location_id: any): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/locations/getLocErrors/' + location_id, { observe: 'body' });
  }

  insertLocError(error: any): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/locations/insertLocError/' + error.location_id, error, {observe:'body'});
  }

  // The backend resolves the market region from location_id; it is returned on
  // the response as Region.
  setCapacity(date: any, locationId: any, customerJob?: any, estimatedtime?: any): Observable<any> {
    return this.http.post<any>(this.baseURL + '/rhAPI/jobEntry/setCapacity', {date: date, location_id: locationId, customerJob: customerJob, estimatedtime: estimatedtime}, { observe: 'body' });
  }

  checkCapacity(date: string, locationId: any, customerJob?: any, estimatedtime?: any): Observable<any> {
    return this.http.post<any>(this.baseURL + '/rhAPI/jobEntry/checkCapacity', {date: date.toString().substring(0,10), location_id: locationId, customerJob: customerJob, estimatedtime: estimatedtime}, { observe: 'body' });
  }

  // Read-only day-load lookup for the job dialog's Schedule Date indicator.
  // The backend resolves the region itself, so only ids are sent; 0 = not set.
  getScheduledHours(date: string, locationId: any, customerJob: any): Observable<any> {
    return this.http.get<any>(
      this.baseURL + '/rhAPI/jobEntry/getScheduledHours/' + date + '/' + (locationId || 0) + '/' + (customerJob || 0),
      { observe: 'body' });
  }


  getEkotropeProject(projectId: string): Observable<any> {
    this.messager.openSnackBar('Searching for Ekotrope project...', '', 'basicAlert');
    return this.http.get(this.baseURL + '/rhAPI/ekotrope/getProjectRP/' + projectId, { observe: 'body' }).pipe(
      tap((event) => {
        const message = event.Message as unknown as string;
        if (event.Error) {
          this.messager.openSnackBar('ERROR: Invalid Ekotrope project ID', 'Close', 'errorAlert');
        } else {
          this.messager.openSnackBar('Ekotrope project found. Importing...', '', 'successAlert');
        }
      })
    );
  }

  getEkotropePlan(planId: string): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/ekotrope/getPlan/' + planId, { observe: 'body' });
  }

  getEkotropePlanAnalysis(planId: string): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/ekotrope/getPlanAna/' + planId, { observe: 'body' });
  }

  qaReview(locationId, body): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/ekotrope/qaReview/' + locationId, body, { observe: 'body' });
  }

  resetBuilderLocationButton(location_id: any): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/locations/resetBuilderLocationButton/' + location_id, {}, {observe: 'body'});
  }



  getPlanDropdownOptions(planname: string, mode?: string): Observable<any> {
    return this.http.get<any>(this.baseURL + '/rhAPI/plans/getPlans/' + this.searchTerm(planname) + this.searchQuery(mode, AUTOCOMPLETE_LIMIT), { observe: 'body' }).pipe(
      map(res => res.Plans)
    );
  }

  postEkoTropeProject(response: any): Observable<any> {

    const ekomodel: any = {};

    ekomodel.projectid = response.id;
    ekomodel.id = response.id;
    ekomodel.name = response.name;
    ekomodel.createdat = response.createdAt;
    ekomodel.createdby = response.createdBy;
    ekomodel.lastsavedat = response.lastSavedAt;
    ekomodel.lastsavedby = response.lastSavedBy;
    ekomodel.selforplanlastsavedat = response.selfOrPlanLastSavedAt;
    ekomodel.islocked = response.isLocked;
    ekomodel.status = response.status;
    ekomodel.streetaddress = response.location.streetAddress;
    ekomodel.city = response.location.city;
    ekomodel.state = response.location.state;
    ekomodel.zip = response.location.zip;
    ekomodel.zone = response.location.climateZone.zone || '';
    ekomodel.moistureregime = response.location.climateZone.moistureRegime || '';
    ekomodel.notes = response.notes;
    ekomodel.model = response.model || '';
    ekomodel.community = response.community || '';
    ekomodel.builder = response.builder || '';
    ekomodel.builderpermitdateornumber = response.builderPermitDateOrNumber || '';
    ekomodel.ratrname = response.hersRatingDetails.rater.name;
    ekomodel.ratrphone = response.hersRatingDetails.rater.phone;
    ekomodel.ratremail = response.hersRatingDetails.rater.email || '';
    ekomodel.resnetraterid = response.hersRatingDetails.rater.resnetRaterId || '';
    ekomodel.resnetproviderid = response.hersRatingDetails.rater.resnetProviderId || '';
    ekomodel.rcname = response.hersRatingDetails.rater.ratingCompany.name;
    ekomodel.rcid = response.hersRatingDetails.rater.ratingCompany.id;
    ekomodel.registryid = response.hersRatingDetails.registryId || '';
    // $scope.entity.registryid = response.hersRatingDetails.registryId || "";
    ekomodel.ratingdate = response.hersRatingDetails.ratingDate || '';
    ekomodel.fieldratingdate = response.hersRatingDetails.fieldRatingDate || '';
    ekomodel.ratingtype = response.hersRatingDetails.ratingType;
    ekomodel.masterplanid = response.masterPlanId;
    ekomodel.resultsunchangedsince = response.resultsUnchangedSince;
    ekomodel.submittedplanid = response.submittedPlanId;
    ekomodel.algorithmversion = response.algorithmVersion;
    ekomodel.thirdparty = response.thirdParty || '';
    ekomodel.associatedusers = {
      associatedusers : response.hersRatingDetails.associatedUsers
    };
    ekomodel.utilitysubmissionhistory = {
      utilitysubmissionhistory: response.utilitySubmissionHistory
    };


    return this.http.post(this.baseURL + '/rhAPI/ekotrope/importProject', ekomodel).pipe(
      tap((event) => {
        const message = event.Message as unknown as string;
        if (event.Error) {
          this.messager.openSnackBar(message, 'Close', 'errorAlert');
        } else {
          //this.messager.openSnackBar(message + '...Ekotrope project imported', '', 'successAlert');
        }
      })
    );

  }

  postEkotropeUtilityRates(pid: string, object): Observable<any> {
    const utility = {
      projectid: pid,
      fuel: object.fuel,
      name: object.name
    };
    return this.http.post(this.baseURL + '/rhAPI/ekotrope/importUtRates', utility).pipe(
      tap((event) => {
        const message = event.Message as unknown as string;
        if (event.Error) {
          this.messager.openSnackBar(message, 'Close', 'errorAlert');
        } else {
          //this.messager.openSnackBar(message + '...Utility rates imported', '', 'successAlert');
        }
      })
    );
  }


  postEkotropePlanAnalysis(pid: string, response): Observable<any> {

    const ekoAna: any = {};

    ekoAna.plan_id = response.id;
    ekoAna.project_id = pid;
    ekoAna.name = JSON.stringify(response.name);
    ekoAna.emissions = JSON.stringify(response.emissions);
    ekoAna.herscore = JSON.stringify(response.hersScore);
    ekoAna.hersscorenopv = JSON.stringify(response.hersScoreNoPv);
    ekoAna.carbonindex = JSON.stringify(response.carbonIndex);
    ekoAna.buildingtype = JSON.stringify(response.buildingType);
    ekoAna.energy = JSON.stringify(response.energy);
    ekoAna.compliance = {
      compliance: response.compliance
    };
    ekoAna.compliance = JSON.stringify(ekoAna.compliance);
    return this.http.post(this.baseURL + '/rhAPI/ekotrope/importAna', ekoAna).pipe(
      tap((event) => {
        const message = event.Message as unknown as string;
        if (event.Error) {
          this.messager.openSnackBar(message, 'Close', 'errorAlert');
        } else {
          //this.messager.openSnackBar(message + '...Plan analysis imported', '', 'successAlert');
        }
      })
    );

  }

  postEkotropeHousePlan(pid: string, response): Observable<any> {

    const ekoPlan: any = {};
    ekoPlan.plan_id = response.id;
    ekoPlan.project_id = pid;
    ekoPlan.lighting = JSON.stringify(response.lighting);
    ekoPlan.thermalenvelope = JSON.stringify(response.thermalEnvelope);
    ekoPlan.appliances = JSON.stringify(response.appliances);
    ekoPlan.mechanicals = JSON.stringify(response.mechanicals);
    ekoPlan.onsitegenerations = JSON.stringify(response.onsiteGenerations);
    ekoPlan.watersystem = JSON.stringify(response.waterSystem);
    ekoPlan.details = JSON.stringify(response.details);

    return this.http.post(this.baseURL + '/rhAPI/ekotrope/importPlan/', ekoPlan).pipe(
      tap((event) => {
        const message = event.Message as unknown as string;
        if (event.Error) {
          this.messager.openSnackBar(message, 'Close', 'errorAlert');
        } else {
          //this.messager.openSnackBar(message + '...Project plan imported', '', 'successAlert');
        }
      })
    );

  }

  /**
 * Get Location List
 * @returns
 */
  getLocationList(offset) {


    //return this.http.get(`${this.baseURL}/rhAPI/locations/${this.auth.getContractorInfo().contractor_id}`);
    //paginated version
    return this.http.get(`${this.baseURL}/rhAPI/locationspaginated/${this.auth.getContractorInfo().contractor_id}/${offset}`);

  }

  /**
   * Get all Locations
   */

  getLocationsAll() {
    return this.http.get(`${this.baseURL}/rhAPI/locations/${this.auth.getContractorInfo().contractor_id}`);
  }

  /**
* Get Location List
* @returns
*/
  getLocationAddress(searchAddress: string) {
    return this.http.get(`${this.baseURL}/rhAPI/locations/getLocationsSA/${searchAddress}`);
  }
  deleteJob(job_id): void {

    const jobs = this.jobsubject.getValue();
    const jobIndex  = jobs.findIndex(job => job.job_id === job_id);

    const newJobList = jobs.slice(0);

    newJobList.splice(jobIndex, 1);
    this.jobsubject.next(newJobList);

  }

  sendResultsEmail(data): Observable<any> {

    return this.http.post('/rhAPI/emails/hvac',data,{observe:'body'});

  }




  filterAddress(element) {
    return fromEvent(element, 'keyup').pipe(
      map((event: any) => {
        return event?.target?.value;
      }),
      filter((res: any) => res.length),
      debounceTime(1000),
      distinctUntilChanged(),
      mergeMap((text: string) => this.getLocationAddress(text))
    );
  }

  /**
* Get Location List
* @returns
*/
  getCity(searchAddress: string) {
    return this.http.get(`${this.baseURL}/rhAPI/city/getDropdownNames/${searchAddress}`);
  }


  filterCity(element) {
    return fromEvent(element, 'keyup').pipe(
      map((event: any) => {
        return event?.target?.value;
      }),
      filter((res: any) => res.length),
      debounceTime(1000),
      distinctUntilChanged(),
      mergeMap((text: string) => this.getCity(text))
    );
  }


  /**
* Get Location List
* @returns
*/
  getPlans(searchAddress: string) {
    return this.http.get(`${this.baseURL}/rhAPI/plans/getPlans/${searchAddress}`);
  }


  filterPlan(element) {
    return fromEvent(element, 'keyup').pipe(
      map((event: any) => {
        return event?.target?.value;
      }),
      filter((res: any) => res.length),
      debounceTime(1000),
      distinctUntilChanged(),
      mergeMap((text: string) => this.getPlans(text))
    );
  }


  /**
* Get Location List
* @returns
*/
  getSubDivisionName(subDivisionName: string) {
    return this.http.get(`${this.baseURL}/rhAPI/locations/getLocationsSub/${subDivisionName}`);
  }


  filterSubDivision(element) {
    return fromEvent(element, 'keyup').pipe(
      map((event: any) => {
        return event?.target?.value;
      }),
      filter((res: any) => res.length),
      debounceTime(1000),
      distinctUntilChanged(),
      mergeMap((text: string) => this.getSubDivisionName(text))
    );
  }



  /**
* Get Location List
* @returns
*/
  getselectedDesigns(subDivisionName: string) {
    return this.http.get(`${this.baseURL}/rhAPI/locations/getLocationsSub/${subDivisionName}`);
  }


  filterLocationDesigns(element) {
    return fromEvent(element, 'keyup').pipe(
      map((event: any) => {
        return event?.target?.value;
      }),
      filter((res: any) => res.length),
      debounceTime(1000),
      distinctUntilChanged(),
      mergeMap((text: string) => this.getselectedDesigns(text))
    );
  }

  /**
* Get All Address Location
* @returns
*/
  getAllAddress(locationAddress: String | number) {
    return this.http.get(
      `${this.baseURL}/rhAPI/locations/getAllAddress/${locationAddress}`
    );
  }

  resetLocation(location_id: String | number){
    return this.http.post(this.baseURL + '/rhAPI/resetLocation/' + location_id, '', {observe:'body'});
  }

  //getAPlan


  //getHVAC Contractor
  getHvacContractor(hvacContractor): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/getHvacContractor/' + hvacContractor, {observe: 'body'});
  }

  getHvacContractors(): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/customers/hvac', {observe: 'body'});
  }

  getHvacContractorDropdown(name): Observable<any> {
    return this.http.get<any>(this.baseURL + '/rhAPI/customers/hvac/' + name, { observe: 'body' });
  }

  /**
* Get All SubDivision Location
* @returns
*/
  getAllSubDivisionName(locationSubDivision: String | number) {
    return this.http.get(
      `${this.baseURL}/rhAPI/locations/getAllSub/${locationSubDivision}`
    );
  }

  /**
* Get All Address Location
* @returns
*/
  getAllCities(locationCities: String | number) {
    return this.http.get(
      `${this.baseURL}/rhAPI/locations/getAllCities/${locationCities}`
    );
  }

  /**
* Get All Address Location
* @returns
*/
  getAllPlans(locationPlans: String | number) {
    return this.http.get(
      `${this.baseURL}/rhAPI/locations/getAllPlans/${locationPlans}`
    );
  }

  updateErrorsFalse(locID){
    return this.http.post(this.baseURL + '/rhAPI/locations/hvacErrorsFalse/' + locID,{}, {observe:'body'});
  }

  /**
* Get One Location
* @returns
*/
  getSingleLocation(locationId: String | number) {
    return this.http.get(
      `${this.baseURL}/rhAPI/onelocation/${locationId}`
    );
  }

  /**
 * Add New Location
 * @param equipment
 * @returns
 */
  addNewLocation(location: LocationModel) {
    return this.http.post(`${this.baseURL}/rhAPI/addLocations`, location);
  }

  viewAllLocations() {
    return this.http.get(
      `${this.baseURL}/rhAPI/locations/`
    );
  }

  selectedDesignLocation(selectedDesign: DesignsEntity, selectedLocation: LocationsEntity, reNavigate: boolean) {
    let hvac_design_id = Number(selectedDesign.id);
    let location_id = Number(selectedLocation.location_id);
    let hid = hvac_design_id;
    let sbmtHvId = 0;
    let sbmtSys = 0;
    let sbmtSyss = [];
    let design :any;
    let plan_id = selectedLocation.plan;
    let plan: any;


    this.http.get(`${this.baseURL}/rhAPI/getDesign/` + hid).subscribe((response: any) => {
      design = response.hvac;
      this.http.post(`${this.baseURL}/rhAPI/stampDesign/` + location_id, response.hvac[0]).subscribe((response: any) => {
        sbmtHvId = response.Id;
        selectedLocation.sbmt_dsn_id = response.Id;
        this.http.get(`${this.baseURL}/rhAPI/getSystemForSub/` + hid).subscribe((response: any) => {
          response.systems.forEach((system) => {
            this.http.post(`${this.baseURL}/rhAPI/stampSystem/` + sbmtHvId, system).subscribe((response: any) => {
              sbmtSys = response.Id;
              sbmtSyss.unshift(sbmtSys);

              if (response.Rows == 0) {
                this.snackBar.open(`'This design does not have any systems. Please go back and enter system and equipment data.' `, "", {
                  duration: 2000,
                });
                // alert('This design does not have any systems. Please go back and enter system and equipment data.')
              }

              this.http.get(`${this.baseURL}/rhAPI/getOnePiece/` + system.equipment_id).subscribe((response: any) => {
                this.http.post(`${this.baseURL}/rhAPI/stampEquip/` + sbmtSyss.pop(), response.Equipment).subscribe((response: any) => {
                  //original Kambda function

                  //get plan first
                  this.http.get(this.baseURL + '/rhAPI/location/getRaterHVAC/' + plan_id).subscribe(
                    (res:any) => {
                      plan = res.Plan;
                    })

                  this.http.post(this.baseURL + '/rhAPI/locations/attachHvacLocation', selectedLocation).subscribe((res) => {
                    //remove the location:
                    this.snackBar.open(`'Location assigned successfully.' `, "", {
                      duration: 2000,
                    });
                    // .success('Location assigned successfully.');
                    // $state.go('hvacLocations');
                    if(reNavigate == true){
                      this.router.navigate(["/hvacLocations"])
                    }
                    return;

                  });

                });
              });

            });

          });//end forEach
        });
      });//stamp design
    });
  }

  getFormlogs(id: number): Observable<any> {
    return this.http.get<any>(this.baseURL + '/rhAPI/raterform/getFormlogs/' + id, { observe: 'body' })
  }

  sendGasRebateEmail(location_id: number): Observable<any> {
    return this.http.post<any>(this.baseURL + '/rhAPI/emails/gasRebateJob/' + location_id, {}, { observe: 'body' })
  }

  getFormLogsSingle(id: number): Observable<any> {
    return this.http.get<any>(this.baseURL + '/rhAPI/raterform/getFormlogsSingle/' + id, { observe: 'body' })
  } 

  getFormLogJobs(loc_id: number):Observable<any> {
    return this.http.get<any>(this.baseURL + '/rhAPI/raterform/getJobTypesAndDates/' + loc_id, { observe: 'body' });
  }

  updateTrackJobDates(track, jobid, locid): Observable<any> {
    return this.http.post(this.baseURL + `/rhAPI/jobEntry/updateTrackDates/${track}/${jobid}/${locid}`, { observe: 'body' });
  }

  updateTrackJobDatesBeechwood(jobid, locid): Observable<any> {
    return this.http.post(this.baseURL + `/rhAPI/location/updateTrackDatesBeechwood/${locid}/${jobid}`, { observe: 'body' });
  }

  getElectricUtilities(): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/locations/getElectricUtilities', { observe: 'body' });
  }

  getGasUtilities(): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/locations/getGasUtilities', { observe: 'body' });
  }

  addUtility(type: 'gas' | 'electric', company: string, who: string): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/locations/addUtility', { type, company, who }, { observe: 'body' });
  }

  getEmailLogs(loc_id): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/raterform/getEmailLogs/' + loc_id, { observe: 'body' });
  }

  getJobTypeCerts(customerjob_id):Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/customerjobs/getJobTypeCerts/' + customerjob_id, { observe: 'body' });
  }

  getEmployeeCerts(employee_id): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/customerjobs/getEmployeeCerts/' + employee_id, { observe: 'body' });
  }

  logTrackJob(jobid): Observable<any> {
    return this.http.get(this.baseURL + `/rhAPI/tracks/logTrackJob/${jobid}`,  { observe: 'body' });
  }

}
